import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MapPin, Plus, ShieldAlert, Sprout } from 'lucide-react';
import {
  type Column,
  Drawer, LiveScreen, QueryState, RecordTable, SearchBox, StatusPill, formatDateTime, useDebounced,
} from '@/components/live';
import { EcosystemFlow, type EcosystemGroup } from '@/screens/parts/EcosystemFlow';
import { ChainStepEditor } from '@/screens/parts/ChainStepEditor';
import { AddEntityToGroup, type Candidate } from '@/screens/parts/AddEntityToGroup';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { callRpc } from '@/api/resources';
import { isSupabaseConfigured } from '@/lib/supabase';

/**
 * The ecosystem: every actor the organisation deals with, in one place.
 *
 * Owner, 2026-09-26: "first build ecosystem screen managment for the client admin?"
 *
 * Until now an admin asking "who is in my chain, and which of them is out of compliance?" had to
 * visit the contractor register, the stakeholder register and the work sites register and hold the
 * answer in their head - and a transporter who was none of those three appeared on no screen at
 * all. This is the one list, read from `api.ecosystem_entities`.
 *
 * WHAT AN ENTITY IS HERE. A party, and its ROLES. Not a type: a company that hauls timber and also
 * saws it appears ONCE holding transporter and processor, rather than twice under two headings,
 * and a role that ended stops counting on its end date without anybody editing a record. That is
 * why the role filters below are overlapping rather than exclusive - a row can answer to two of
 * them, and pretending otherwise would make the counts lie.
 *
 * READ-ONLY, deliberately. Adding a party, granting a role and enabling a login each have their own
 * guarded path already; this is the directory that tells an admin which of those to go and use.
 */

interface EcosystemRow {
  id: string;
  party_code: string | null;
  display_name: string;
  legal_name: string | null;
  party_category: string;
  status: string;
  risk_status: string | null;
  geography_name: string | null;
  primary_mobile: string | null;
  primary_email: string | null;
  role_types: string[] | null;
  roles_label: string | null;
  site_count: number;
  site_types: string[] | null;
  login_enabled: boolean;
  account_count: number;
  last_login_at: string | null;
  never_signed_in: boolean;
  is_grower_only: boolean;
  chain_side: string;
  chain_groups: string[] | null;
  address: string | null;
  description: string | null;
  consent_live: boolean;
  kyc_health: string | null;
  created_at: string;
}

/**
 * Six chips, in the order material moves (owner, 2026-09-26: "may we can simply show 6 chip
 * buttons as per our grouping? on-click of chips: we can show table list").
 *
 * Simpler than the two sections it replaces, and than the cards before those: one control, one
 * table. The chips OVERLAP - an actor holding roles in two groups answers to two chips - so the
 * counts do not add up to the total, which is the truth rather than a rounding error.
 */
const CHIPS = [
  { key: 'inputs', label: 'Inputs' },
  { key: 'services', label: 'Services' },
  { key: 'plantation', label: 'Plantation' },
  { key: 'intermediaries', label: 'Intermediaries' },
  { key: 'processing', label: 'Processing' },
  { key: 'buyers', label: 'Buyers' },
];

const CHIP_BLURB: Record<string, string> = {
  inputs: 'Nurseries and material suppliers - what reaches the plantation before anything grows.',
  services: 'Labour contractors, machinery, irrigation and advice.',
  plantation: 'The members who grow the timber. Counted here; listed on their own register.',
  intermediaries: 'Hauliers and agents - the hops between the plantation and a mill.',
  processing: 'Mills, seasoning chambers and treatment plants.',
  buyers: 'Buyers and exporters - where the output goes.',
};

/**
 * The columns the owner asked for: entity name and address, geography, activity, description -
 * plus the two signals that need chasing, which were stat cards until the chips replaced them and
 * had to land somewhere a reader would still see them.
 *
 * Address and description come from whichever domain record introduced the party (a contractor's
 * own address, or its primary site's). A party with neither shows a dash, which is honest: the
 * register knows who they are and not yet where they are.
 */
const COLUMNS: Array<Column<EcosystemRow>> = [
  {
    key: 'display_name',
    label: 'Entity and address',
    render: (row) => (
      <span className="cell-stack">
        <strong>{row.display_name}</strong>
        <small className="muted">
          {[row.party_code,
            row.legal_name && row.legal_name !== row.display_name ? row.legal_name : null,
            row.address]
            .filter(Boolean).join(' · ') || 'no address recorded'}
        </small>
      </span>
    ),
  },
  { key: 'geography_name', label: 'Geography', render: (row) => row.geography_name ?? '--' },
  {
    key: 'roles_label',
    label: 'Activity',
    // No role is the interesting case, not an empty cell: the entity is on the register and the
    // chain does not know what it does.
    render: (row) => (row.roles_label
      ? <span>{row.roles_label}</span>
      : <span className="badge badge-amber">no role held</span>),
  },
  {
    key: 'description',
    label: 'Description',
    render: (row) => (row.description
      ? <span className="cell-clamp">{row.description}</span>
      : <span className="muted">--</span>),
  },
  {
    key: 'kyc_health',
    label: 'KYC',
    render: (row) => (row.kyc_health && row.kyc_health !== 'none_required'
      ? <StatusPill value={row.kyc_health} />
      : <span className="muted">none required</span>),
  },
  {
    key: 'login_enabled',
    label: 'Sign-in',
    render: (row) => {
      if (row.account_count === 0) return <span className="muted">no account</span>;
      if (row.never_signed_in) return <span className="badge badge-amber">never used</span>;
      return row.login_enabled
        ? <span className="badge badge-green">enabled</span>
        : <span className="badge badge-gray">disabled</span>;
    },
  },
];

export default function EcosystemScreen() {
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);
  const [stat, setStat] = useState<string | null>(null);
  const [open, setOpen] = useState<EcosystemRow | null>(null);
  // The stage whose + was pressed; null while the step editor is shut.
  const [addingAt, setAddingAt] = useState<string | null>(null);
  // The group the add button targets; null while the picker is shut.
  const [adding, setAdding] = useState<string | null>(null);

  // Granting a role is a WRITE on the party register, and a viewer does not have it. The database
  // refuses them - verified: "Your role does not allow that" - but showing the button anyway meant
  // a viewer chose a role, chose a company and pressed Add before being told. Failing safely is
  // not the same as failing kindly.
  const { profile } = useAuth();
  const mayAdd = can(profile, 'party.register', 'create');

  // Members are COUNTED, not listed (owner, 2026-09-26: "At the ecosystem level, can mention
  // farmers (no need to list all the farmers)"). Counted in SQL as well, because `listRows`
  // returns at most 200 and a tally taken from a page would report 200 members to a group with
  // five thousand.
  const summary = useQuery({
    queryKey: ['ecosystem-summary'],
    queryFn: async () => (await callRpc<Array<{
      growers: number; growers_with_plots: number; chain_entities: number;
      entities_no_role: number; entities_kyc_unfit: number; entities_never_signed_in: number;
    }>>('fn_ecosystem_summary', {}))?.[0] ?? null,
    enabled: isSupabaseConfigured,
  });

  const entities = useRows<EcosystemRow>('ecosystem_entities', {
    search: debounced
      ? { columns: ['display_name', 'legal_name', 'party_code', 'primary_mobile', 'primary_email'], term: debounced }
      : undefined,
    orderBy: { column: 'display_name', ascending: true },
    limit: 200,
  });

  const chain = useMemo(
    () => (entities.data ?? []).filter((row) => !row.is_grower_only),
    [entities.data],
  );
  // Filtered by the stage chosen on the chart. The cards that used to do this were removed
  // (owner, 2026-09-26: "far better, remove the stat cards"): they showed the chart's own six
  // numbers a second time, and two controls over one table leaves the reader working out which
  // of them it is obeying.
  const visible = useMemo(
    () => (stat ? chain.filter((row) => (row.chain_groups ?? []).includes(stat)) : chain),
    [chain, stat],
  );

  // The chart's numbers come from SQL, not from the 200 rows on screen.
  const groups = useQuery({
    queryKey: ['ecosystem-groups'],
    queryFn: () => callRpc<EcosystemGroup[]>('fn_ecosystem_groups', {}),
    enabled: isSupabaseConfigured,
  });

  // The traceability MODEL: how many steps are defined at each stage. A different question from
  // how many actors are there, and shown separately on the node for that reason.
  const steps = useRows<{ id: string; chain_group: string }>('chain_step_templates', { limit: 200 });
  const stepCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const step of steps.data ?? []) {
      counts[step.chain_group] = (counts[step.chain_group] ?? 0) + 1;
    }
    return counts;
  }, [steps.data]);

  // Entities offered by the add drawer: everyone on the register, growers included, because a
  // grower that also hauls is a legitimate thing to add to intermediaries.
  const candidates = useMemo<Candidate[]>(
    () => (entities.data ?? []).map((row) => ({
      id: row.id,
      display_name: row.display_name,
      legal_name: row.legal_name,
      party_code: row.party_code,
      party_category: row.party_category,
      geography_name: row.geography_name,
      role_types: row.role_types,
    })),
    [entities.data],
  );

  const countFor = (key: string) =>
    (groups.data ?? []).find((g) => g.chain_group === key)?.entity_count ?? 0;

  return (
    <LiveScreen
      subtitle="Everyone the chain passes through: who they are, what they do, whether their papers are in order and whether they can sign in. Members are counted above."
      title="Ecosystem"
    >
      <div className="toolbar-row">
        <SearchBox onChange={setSearch} placeholder="Name, code, mobile or email" value={search} />
      </div>

      {/* The members, as one line rather than five thousand rows. It links to the register that
          IS built for them, so nothing is hidden - only kept where it belongs. */}
      <section className="card">
        <div className="section-heading">
          <h3><Sprout size={15} /> Members</h3>
          <Link to="/farmers">Open the farmer register</Link>
        </div>
        <p className="muted">
          {summary.isLoading
            ? 'Counting...'
            : summary.data
              ? `${summary.data.growers} member${summary.data.growers === 1 ? '' : 's'} supply this chain, `
                + `${summary.data.growers_with_plots} with a mapped plot.`
              : 'Members could not be counted.'}
          {' '}They are the origin of everything below, and are managed on their own register.
        </p>
      </section>

      {/* COLLAPSED by default (owner, 2026-09-26: "this may simplify?"). The chips answer "who is
          in this group", which is the everyday question, and two controls filtering one table is
          what made the stat cards worth removing. The flow answers a rarer and different one -
          where the gaps are, and what is meant to happen at each stage - so it is kept and folded
          away rather than deleted. It drives the SAME selection as the chips, so opening it does
          not introduce a second source of truth. */}
      <details className="card eco-flow-fold">
        <summary>How material moves, and the steps defined at each stage</summary>
        <EcosystemFlow
          groups={groups.data ?? []}
          onAddStep={setAddingAt}
          onSelect={setStat}
          selected={stat}
          stepCounts={stepCounts} />
        <ChainStepEditor
          entities={(entities.data ?? []).map((row) => ({
            id: row.id, display_name: row.display_name, party_code: row.party_code,
          }))}
          group={addingAt}
          onClose={() => setAddingAt(null)} />
      </details>

      {/* One table per side. The columns are defined ONCE above: two copies would drift, and the
          first thing to drift is always the column somebody added to only one of them. */}
      {/* One control, one table. The chips filter; the button beside them adds to whichever
          group is chosen, which is the only place an "add" has an unambiguous target. */}
      <section className="card">
        <div className="section-heading">
          <h3>Groups</h3>
          {stat && mayAdd && (
            <button className="primary" onClick={() => setAdding(stat)} type="button">
              <Plus size={14} /> Add to {CHIPS.find((c) => c.key === stat)?.label}
            </button>
          )}
        </div>
        <div className="chip-row">
          {CHIPS.map((chip) => (
            <button
              aria-pressed={stat === chip.key}
              className={`chip${stat === chip.key ? ' is-on' : ''}`}
              key={chip.key}
              // Pressing the chosen chip clears it, so the filter can be put down.
              onClick={() => setStat(stat === chip.key ? null : chip.key)}
              type="button"
            >
              {chip.label}
              <span className="chip-count">{countFor(chip.key)}</span>
            </button>
          ))}
        </div>
        {stat && <p className="muted">{CHIP_BLURB[stat]}</p>}
      </section>

      {stat === 'plantation' ? (
        <section className="card">
          <p className="state-note">
            Members are counted above but are not listed here - a group can have thousands of
            them. <Link to="/farmers">Open the farmer register</Link> to work with them.
          </p>
        </section>
      ) : (
        <QueryState empty="Nobody is on the register yet." query={entities}>
          <section className="card">
            <div className="section-heading">
              <h3>{stat ? CHIPS.find((c) => c.key === stat)?.label : 'Everyone in the chain'}</h3>
              <span className="muted">{visible.length}</span>
            </div>
            {visible.length === 0
              ? (
                <p className="state-note">
                  Nobody holds a role in this group yet.
                  {mayAdd
                    ? ' Use the add button above to give an entity that is already on the register the role that puts it here.'
                    : ' An organisation admin can add one.'}
                </p>
              )
              : (
                <RecordTable
                  columns={COLUMNS}
                  onRowClick={setOpen}
                  rows={visible}
                  selectedId={open?.id ?? null} />
              )}
          </section>
        </QueryState>
      )}

      <AddEntityToGroup
        candidates={candidates}
        group={adding}
        onClose={() => setAdding(null)} />

      <Drawer
        onClose={() => setOpen(null)}
        open={Boolean(open)}
        subtitle={open?.party_code ?? undefined}
        title={open?.display_name ?? ''}
      >
        {open && (
          <div className="page-guide">
            <section>
              <h3>Who they are</h3>
              <ul>
                <li>{open.party_category}{open.legal_name ? ` · ${open.legal_name}` : ''}</li>
                {open.geography_name && <li><MapPin size={12} /> {open.geography_name}</li>}
                {open.primary_mobile && <li>{open.primary_mobile}</li>}
                {open.primary_email && <li>{open.primary_email}</li>}
                <li>On the register since {formatDateTime(open.created_at)}</li>
              </ul>
            </section>

            <section>
              <h3>What they do</h3>
              {(open.role_types ?? []).length === 0
                ? (
                  <p>
                    No role is recorded, so nothing in the chain can refer to this party by what it
                    does. Give it a role on the party register before using it in a transaction.
                  </p>
                )
                : <ul>{(open.role_types ?? []).map((role) => <li key={role}>{role}</li>)}</ul>}
              {open.site_count > 0 && (
                <p>
                  Operates {open.site_count} site{open.site_count === 1 ? '' : 's'}
                  {(open.site_types ?? []).length ? ` (${(open.site_types ?? []).join(', ')})` : ''}.
                </p>
              )}
            </section>

            <section>
              <h3>Standing</h3>
              <ul>
                <li>Record status: {open.status}</li>
                {open.risk_status && <li>Risk: {open.risk_status}</li>}
                <li>KYC: {open.kyc_health ?? 'unknown'}</li>
                <li>
                  Data-processing consent: {open.consent_live ? 'live' : 'none live'}
                </li>
              </ul>
              {!open.consent_live && open.party_category === 'individual' && (
                <p>
                  <ShieldAlert size={12} /> Without a live consent nothing may be sent to this
                  person, including a request to consent again if they have withdrawn.
                </p>
              )}
            </section>

            <section>
              <h3>Sign-in</h3>
              {open.account_count === 0
                ? <p>No account. This party cannot sign in and has no view of its own records.</p>
                : (
                  <ul>
                    <li>{open.account_count} account{open.account_count === 1 ? '' : 's'}</li>
                    <li>{open.login_enabled ? 'Sign-in is enabled' : 'Sign-in is disabled'}</li>
                    <li>
                      {open.last_login_at
                        ? `Last signed in ${formatDateTime(open.last_login_at)}`
                        : 'Never signed in since the account was made'}
                    </li>
                  </ul>
                )}
            </section>
          </div>
        )}
      </Drawer>
    </LiveScreen>
  );
}
